// @ts-strict-ignore
import { useGetRbacCustomers } from '~/dashboard/admin/provisioning/helpers/useGetCustomerRbacData';
import { useGetCustomers } from '~/dashboard/admin/provisioning/helpers/useGetCustomers';
import { useCreateCustomerMutation } from '~/dashboard/admin/provisioning/helpers/useCreateCustomerMutation';
import { IconName } from '@usealloy/component-library';
import { UserContext } from '~/dashboard/context';
import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { libraryRoutes } from '~/helpers/api-new/modules/library/libraryRoutes';
import { RULE_LIBRARY_ORGANIZATION_SLUG } from '~/constants';
import {
  buildRuleLibrarySessionUrl,
  isNonProdRuleLibraryHost,
  PRIMARY_RULE_LIBRARY_BASE_URL,
  PRIMARY_RULE_LIBRARY_CUSTOMER_ID,
} from '~/dashboard/admin/library/global-repository/utils';
import { routes } from '~/helpers/routes';
import { StyledIcon } from './SidebarStyle';

export const AdminSidebarLinks = () => {
  const {
    agent: { isEmployee }, featureFlags, agent, alloyEnvironment,
  } = useContext(UserContext);
  const location = useLocation();
  const getNavLinkClass = path => (location.pathname.match(new RegExp(`^${path}`)) ? 'active' : '');
  const {
    customers, globalServices, notifications, employees, ai,
  } = routes.v3.dashboard.admin;

  const shouldShowLibrary = (
    featureFlags?.ruleLibraryPhase >= 1
  );

  // Move hooks to component level
  const {
    data: { cells } = { cells: {} },
  } = useGetCustomers();

  const {
    data: customerRbacData = [],
    refetch: refetchCustomerRbacData,
  } = useGetRbacCustomers({
    cells,
    agent,
  }, {
    enabled: !!Object.keys(cells ?? {}).length,
  });

  const { mutateAsync: createCustomerMutation } = useCreateCustomerMutation();

  const getLibraryCustomer = () => customerRbacData.find(
    rbac => rbac.organization_name_slug === RULE_LIBRARY_ORGANIZATION_SLUG,
  );
  const handleLibraryClick = async e => {
    e.preventDefault();
    const hostName = window.location.hostname;

    // Log users into the main prod alloy cell and utilize the `rule-library` customer
    // This is for the creation of a global rule library that will be used by all customers
    let cellHostUrl = PRIMARY_RULE_LIBRARY_BASE_URL;
    let customerId = PRIMARY_RULE_LIBRARY_CUSTOMER_ID;

    // TODO: figure out a robust way to determine what environment this is in
    if (isNonProdRuleLibraryHost(hostName)) {
      cellHostUrl = window.location.origin;
      let ruleLibraryCustomer = await getLibraryCustomer();

      if (!ruleLibraryCustomer) {
        // In local development, automatically create the rule-library customer
        if (hostName.includes('localhost')) {
          await createCustomerMutation({
            organization_name: 'Rule Library',
            organization_name_slug: RULE_LIBRARY_ORGANIZATION_SLUG,
            cell: alloyEnvironment,
            is_controlled_child: false,
            parent_organization_slugs: [],
            description: 'Global rule library for policy management',
          });

          // Refetch to get the newly created customer
          const { data: updatedRbacData } = await refetchCustomerRbacData();
          ruleLibraryCustomer = updatedRbacData?.find(
            rbac => rbac.organization_name_slug === RULE_LIBRARY_ORGANIZATION_SLUG,
          );

          if (!ruleLibraryCustomer) {
            return;
          }
        } else {
          return;
        }
      }
      customerId = ruleLibraryCustomer.customer_id;
    }

    window.location.assign(
      buildRuleLibrarySessionUrl(cellHostUrl, customerId, libraryRoutes.globalRepository.view),
    );
  };

  return (
    <>
      <li className={`${getNavLinkClass(customers.list)} flex center`}>
        <span className="active-bar" />
        <a href={`${window.location.origin}${customers.list}`} className="flex">
          <StyledIcon icon={IconName.Customers} />
          <span className="text fs-unmask">Customers</span>
        </a>
      </li>
      {isEmployee && (
        <>
          <li className={`${getNavLinkClass(globalServices.list)} flex center`}>
            <span className="active-bar" />
            <a href={`${window.location.origin}${globalServices.list}`} className="flex">
              <StyledIcon icon={IconName.Service} />
              <span className="text fs-unmask">Services</span>
            </a>
          </li>

          <li className={`${getNavLinkClass(notifications.list)} flex center`}>
            <span className="active-bar" />
            <a href={`${window.location.origin}${notifications.list}`} className="flex">
              <StyledIcon icon={IconName.Email} />
              <span className="text fs-unmask">Notifications</span>
            </a>
          </li>

          <li className={`${getNavLinkClass(employees.list)} flex center`}>
            <span className="active-bar" />
            <a href={`${window.location.origin}${employees.list}`} className="flex">
              <StyledIcon icon={IconName.Employees} />
              <span className="text fs-unmask">Employees</span>
            </a>
          </li>

          {shouldShowLibrary && (
            <li className={`${getNavLinkClass(libraryRoutes.globalRepository.view)} flex center`}>
              <span className="active-bar" />
              <a
                href={`${window.location.origin}${libraryRoutes.globalRepository.view}`}
                className="flex"
                onClick={handleLibraryClick}
              >
                <StyledIcon icon={IconName.Library} />
                <span className="text fs-unmask">Policy Library</span>
              </a>
            </li>
          )}

          <li className={`${getNavLinkClass(ai.view)} flex center`}>
            <span className="active-bar" />
            <a href={`${window.location.origin}${ai.view}`} className="flex">
              <StyledIcon icon={IconName.Sparkles} />
              <span className="text fs-unmask">AI</span>
            </a>
          </li>
        </>
      )}
    </>
  );
};
